import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import {
  hasAdminSession, hasShopSession, readAdminProfile, readShopProfile, cacheAdminProfile, cacheShopProfile,
  startAdminSession, startShopSession, clearAdminSession, clearShopSession,
} from "@/lib/session";

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [shop, setShop] = useState(() => readShopProfile());
  const [admin, setAdmin] = useState(() => readAdminProfile());
  const [cartCount, setCartCount] = useState(0);
  const [wishlistIds, setWishlistIds] = useState([]);

  // Derived synchronously from the non-secret marker cookies (lib/session.js) so
  // the route guards are correct on the very first render, in every tab.
  const shopLoggedIn = hasShopSession();
  const adminLoggedIn = hasAdminSession();

  // NOTE: The JWT lives ONLY in an httpOnly, Secure, SameSite=Lax cookie set
  // server-side (by /api/shops/login, /api/admin/login, /api/crm/login) -
  // it is never readable by client-side JS, so an XSS bug can't exfiltrate
  // it. Axios sends it automatically via `withCredentials: true` (lib/api.js).
  // The cached display profile (name / role / shop name only) is tab-scoped
  // sessionStorage; when a fresh tab has a session but no cached profile we
  // re-hydrate it from the server.
  useEffect(() => {
    if (adminLoggedIn && !admin) {
      api.get("/crm/me").then(({ data }) => setAdmin(cacheAdminProfile(data))).catch(() => {});
    }
    if (shopLoggedIn && !shop) {
      api.get("/shops/me").then(({ data }) => setShop(cacheShopProfile(data))).catch(() => {});
    }
    // eslint-disable-next-line
  }, []);

  const loginShop = (_token, shopData) => { setShop(startShopSession(shopData)); };
  const logoutShop = () => {
    api.post("/shops/logout").catch(() => {});
    clearShopSession();
    setShop(null);
    setCartCount(0);
    setWishlistIds([]);
  };
  const loginAdmin = (_token, adminData) => { setAdmin(startAdminSession(adminData)); };
  const logoutAdmin = () => {
    api.post("/admin/logout").catch(() => {});
    clearAdminSession();
    setAdmin(null);
  };

  const refreshCart = useCallback(async () => {
    if (!hasShopSession()) return;
    try {
      const { data } = await api.get("/cart");
      setCartCount(data.count || 0);
    } catch (e) { console.error("Failed to refresh cart:", e); }
  }, []);

  const refreshWishlist = useCallback(async () => {
    if (!hasShopSession()) return;
    try {
      const { data } = await api.get("/wishlist");
      setWishlistIds(data.product_ids || []);
    } catch (e) { console.error("Failed to refresh wishlist:", e); }
  }, []);

  useEffect(() => {
    if (shopLoggedIn) {
      refreshCart();
      refreshWishlist();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <AuthContext.Provider value={{
      shop, admin, shopLoggedIn, adminLoggedIn,
      loginShop, logoutShop, loginAdmin, logoutAdmin,
      cartCount, setCartCount, refreshCart,
      wishlistIds, setWishlistIds, refreshWishlist,
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
